import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import {
  Button,
  Chip,
  Dialog,
  DialogActions as _DialogActions,
  DialogContent as _DialogContent,
  DialogTitle as _DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import { ChangeEvent, createElement, FC, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import {
  Creation,
  Creative,
  CreativeFormat,
  CreativeFormatAspectRatio,
  CreativeFormatFastStart,
  CreativeFormatPlaybackTime,
  Deliverable,
  File,
  Issue,
  IssueComment,
  Maybe,
  OfferPricingDetailItem,
  Request,
  RequestItem,
  User,
  UserDirector,
} from '../../../graphql/generated/graphql';
import theme from '../../../theme';
import { toJSDate } from '../../../utility/graphql/datetime';
import AvatarWithName from '../../component/primitive/AvatarWithName';
import { SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import _ModificationContentItem from './ModificationContentItem';

interface Props {
  creation: Pick<Creation, 'state' | 'assignmentId'> & {
    creativeFormat: Pick<CreativeFormat, 'id'> & {
      creativeType: Pick<CreativeFormat['creativeType'], 'key' | 'name'>;
      attribute: {
        playbackTime?: {
          seconds: CreativeFormatPlaybackTime['seconds'];
        } | null;
        aspectRatio?: Pick<CreativeFormatAspectRatio, 'aspectRatio' | 'size'> | null;
        fastStart?: CreativeFormatFastStart | null;
      };
    };
    deliverables: (Pick<Deliverable, 'id'> & {
      creative: {
        file: Pick<File, 'mimeType' | 'url'>;
      };
    })[];
  };
  assets: Array<FileReference>;
  modificationRequests: (Pick<Request, 'id' | 'state' | 'deliveryDue' | 'submittedAt' | 'feeType'> & {
    sender: Pick<UserDirector, 'profileImageUrl' | 'name'>;
  })[];
  selectedModificationRequestId: string;
  onSelectedModificationRequestId: (id: string) => void;
  selectedModificationRequest?: Pick<
    Request,
    'targetDeliverableId' | 'submittedAt' | 'id' | 'state' | 'bucketId' | 'touchedAt'
  > & {
    sender: Pick<UserDirector, 'profileImageUrl' | 'name'>;
    deliverables: (Pick<Deliverable, 'intent' | 'createdAt'> & {
      creative: Pick<Creative, 'id'> & {
        file: Pick<File, 'url' | 'mimeType' | 'fastStart' | 'attribute' | 'originalFileName'>;
      };
      attachedThumbnail?: Maybe<{
        file: Pick<File, 'url' | 'size' | 'originalFileName'>;
      }>;
    })[];
    offerPricingDetail?: Maybe<{
      basicPricings: Array<Pick<OfferPricingDetailItem, 'rewardAmount' | 'rewardCurrency'>>;
      optionPricings: Array<Pick<OfferPricingDetailItem, 'rewardAmount' | 'rewardCurrency'>>;
    }>;
    items: (Pick<RequestItem, 'id' | 'typeName' | 'metadata' | 'sender' | 'assetIds' | 'body'> & {
      issue?: Maybe<
        Pick<Issue, 'id' | 'resolved'> & {
          comments: (Pick<IssueComment, 'assetIds' | 'body' | 'createdAt'> & {
            user: Pick<User, 'name' | 'profileImageUrl'>;
          })[];
        }
      >;
    })[];
  };
  hideSubmissionForm: boolean;
  refresh: () => void;
}

const ModificationContent: FC<Props> = ({
  creation,
  assets,
  modificationRequests,
  selectedModificationRequestId,
  onSelectedModificationRequestId,
  selectedModificationRequest,
  hideSubmissionForm,
  refresh,
}) => {
  const selectedDeliverable = selectedModificationRequest
    ? creation.deliverables.find((deliverable) => deliverable.id === selectedModificationRequest.targetDeliverableId)
    : creation.deliverables[0];

  const relatedDeliverableIndex = selectedModificationRequest
    ? creation.deliverables.findIndex(
        (deliverable) => deliverable.id === selectedModificationRequest.targetDeliverableId,
      )
    : 0;
  // index範囲外だとundefined
  const submittedDeliverable = creation.deliverables[relatedDeliverableIndex + 1];

  const [value, setValue] = useState(selectedModificationRequestId);

  const [open, setOpen] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue((event.target as HTMLInputElement).value);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setValue(selectedModificationRequestId);
  };

  const OnOk = () => {
    setOpen(false);
    onSelectedModificationRequestId(value);
  };

  return (
    <Root id="modificationRequest">
      {selectedModificationRequest ? (
        <VersionArea>
          <Version>
            <Typography variant="subtitle1" color="initial">
              <FormattedMessage id="modification_request.version_select.version" />
              <FormattedDate
                value={toJSDate(selectedModificationRequest.submittedAt)}
                year="numeric"
                month="short"
                day="2-digit"
                hour="2-digit"
                minute="2-digit"
              />
            </Typography>
            <Avatar
              imageUrl={selectedModificationRequest.sender.profileImageUrl}
              name={selectedModificationRequest.sender.name}
              small
            />
          </Version>
          <Button onClick={handleClickOpen} color="primary">
            <FormattedMessage id="modification_request.version_select.select" />({modificationRequests.length})
          </Button>
        </VersionArea>
      ) : null}

      {selectedModificationRequest && selectedDeliverable ? (
        <ModificationContentItem
          creativeFile={selectedDeliverable.creative.file}
          submittedDeliverable={submittedDeliverable}
          creation={creation}
          hasDeliverables={creation.deliverables.length > 0}
          modificationRequest={selectedModificationRequest}
          assets={assets}
          hideForm={hideSubmissionForm}
          refresh={refresh}
        />
      ) : null}

      <Dialog disableBackdropClick disableEscapeKeyDown open={open} onClose={handleClose}>
        <DialogTitle disableTypography>
          <Typography variant="h6">
            <FormattedMessage id="modification_request.version_select.dialog_title" />
          </Typography>
        </DialogTitle>
        <DialogContent>
          <FormControl>
            <RadioGroup value={value}>
              {[
                ...modificationRequests.map((modificationRequest, index) => (
                  <DialogFormControlLabel
                    value={modificationRequest.id}
                    control={<Radio color="primary" onChange={handleChange} />}
                    label={
                      <DialogVersion>
                        <VersionItem>
                          <VersionDate>
                            <Typography variant="h6">
                              <FormattedDate
                                value={toJSDate(modificationRequest.submittedAt)}
                                year="numeric"
                                month="short"
                                day="2-digit"
                                hour="2-digit"
                                minute="2-digit"
                              />
                            </Typography>
                          </VersionDate>
                          {modificationRequest.feeType === 'fee' ? (
                            <FeeChips
                              variant="outlined"
                              label={<FormattedMessage id="modification_request.version_select.fee" />}
                              color="primary"
                            />
                          ) : (
                            <FeeChips
                              variant="outlined"
                              label={<FormattedMessage id="modification_request.version_select.no_fee" />}
                              color="default"
                            />
                          )}
                          {index === 0 ? (
                            <Latest color="primary">
                              <FormattedMessage id="modification_request.version_select.latest" />
                            </Latest>
                          ) : null}
                        </VersionItem>
                        <DialogAvatar
                          imageUrl={modificationRequest.sender.profileImageUrl}
                          name={modificationRequest.sender.name}
                          small
                        />
                      </DialogVersion>
                    }
                    key={`modificationRequest-${index}`}
                  />
                )),
              ]}
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button color="default" onClick={handleClose}>
            <FormattedMessage id="reputation_dialog.cancel" />
          </Button>
          <Button onClick={OnOk} color="primary">
            <FormattedMessage id="modification_request.version_select.ok" />
          </Button>
        </DialogActions>
      </Dialog>
    </Root>
  );
};

const Root = styled('div')({});

const VersionArea = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: SPACING_UNIT * 3,
});

const DialogTitle = styled(_DialogTitle)({
  borderBottom: `1px solid ${theme.palette.grey['200']}`,
});

const DialogContent = styled(_DialogContent)({
  paddingTop: '0px',
  padding: '12px 24px',
});

const DialogActions = styled(_DialogActions)({
  borderTop: `1px solid ${theme.palette.grey['200']}`,
  marginTop: 0,
  paddingTop: SPACING_UNIT * 2,
  paddingBottom: SPACING_UNIT,
  height: 60,
});

const Version = styled('div')({ display: 'flex', alignItems: 'center' });

const DialogVersion = styled('div')({
  alignItems: 'center',
});

const VersionDate = styled('div')({ width: '230px' });

const VersionItem = styled('div')({ display: 'inline-flex', alignItems: 'center' });

const DialogFormControlLabel = styled(FormControlLabel)({
  marginLeft: '0px',
  marginRight: '0px',
  marginBottom: SPACING_UNIT,
});

const FeeChips = styled(Chip)({
  marginLeft: SPACING_UNIT,
});

const ModificationContentItem = styled(_ModificationContentItem)({ marginTop: SPACING_UNIT });

const Avatar = styled(AvatarWithName)({
  marginLeft: SPACING_UNIT,
});

const DialogAvatar = styled(AvatarWithName)({
  marginTop: -SPACING_UNIT / 2,
});

const Latest = styled(Typography)({ marginLeft: SPACING_REGULAR });

export default ModificationContent;
